import { sumMoney } from '../results/sum_money.js';
import { tripGeometryOf } from './explorer_request.js';
import type {
  ExplorerConfirm,
  ExplorerFares,
  ExplorerPick,
  ExplorerState,
} from './explorer_state.js';

/**
 * Seletores da FASE DE TARIFAS e do "Confirmar seleção" sobre o estado do
 * explorer — leitura pura, zero transição. Fora de `explorer_state.ts` porque
 * aquele arquivo está no teto de linhas; a API pública segue a mesma
 * (re-exportados de lá).
 */

const fareIdOf = (fares: ExplorerFares): string =>
  'bundledFareId' in fares ? fares.bundledFareId : fares.outboundFareId;

/** Tarifa de PAR ida-e-volta escolhida para um trecho de ROTEIRO. O fio do roteiro
 *  carrega UM `fareId`, e `fareIdOf` mandaria só a ida: o servidor então recusa a
 *  oferta como combo (`round_trip_offer_in_itinerary`) e a pessoa vê uma seleção
 *  completa virar "não entendi". Melhor o confirm nem armar.
 *
 *  CINTO E SUSPENSÓRIO, não caminho vivo: as pernas do multi são buscadas one-way
 *  (`legSearchParams` omite `returnDate` quando `mode === 'multi_leg'`), então
 *  nenhuma lista de trecho serve combo e nenhuma escada de trecho tem coluna de
 *  volta. O teste desta guarda prova a guarda — não que o caminho aconteça. */
const isPairFare = (fares: ExplorerFares): boolean =>
  !('bundledFareId' in fares) && Boolean(fares.returnFareId);

/**
 * **A tela de tarifas está em cena?** (#2262 — a fase 3 do ida e volta;
 * desde a padronização de 30/09, #3875, a fase de tarifa de TODO modo.)
 *
 * Round trip com as DUAS pernas com voo escolhido: a lista sai e entra
 * "Escolha as tarifas de cada voo". Não pergunta por `fares`: o `fares_picked`
 * que fecha a viagem é despachado DESTA tela, e sair dela no mesmo tick em que
 * ela confirma faria a lista piscar por baixo do checkout que está subindo. Só
 * `pick_removed` (o "Alterar voo" de uma das pernas) volta pra lista.
 *
 * one_way e multi_leg: a perna ATIVA com um voo escolhido SEM tarifa ainda —
 * o pick nu (`{ offerId }`) que a linha grava (`flight_chosen`). A tela some
 * quando o pick ganha preço (`fares_picked`) ou some (`pick_removed`); um pick
 * que o HOST semeou já completo (o card do roteiro, o pick do chat na bancada,
 * sempre com `summary`) NÃO abre a tela: a lista mostra a linha marcada, como
 * sempre, e "Selecionar" reabre a escolha. No multi o `fares_picked` de um
 * trecho intermediário avança a perna ativa para a próxima pendente.
 *
 * O pick FINAL é a exceção pela mesma razão do round trip: `takePickFor`
 * grava a tarifa (`fares_picked`) e confirma (`confirmed`) no MESMO gesto, e
 * a tela tem de ficar de pé sob o checkout que sobe — é nela que o CTA gira
 * enquanto o host cota (`confirming`), e sair dela faria a lista piscar por
 * baixo. `unconfirmed` (o voltar do checkout, `resumeSeq`) devolve a lista com
 * a linha marcada, como o pick semeado (CodeRabbit, #3994).
 */
export function isFaresStage(state: ExplorerState): boolean {
  if (state.request.mode === 'round_trip') {
    return state.chosen[0] !== undefined && state.chosen[1] !== undefined;
  }
  const pick = state.chosen[state.activeLeg];
  if (pick === undefined) return false;
  if (pick.fares === undefined && pick.summary === undefined) return true;
  return state.confirmed;
}

/** A perna cujo pick alimenta a tela de tarifas: a VOLTA no ida e volta (o ref
 *  dela é o PAR), a perna ativa nos outros modos. */
export function faresLegOf(state: ExplorerState): number {
  return state.request.mode === 'round_trip' ? 1 : state.activeLeg;
}

/**
 * Um `fares_picked` no trecho ATIVO fecha a viagem? one_way sempre (uma perna,
 * uma tarifa); RT só na fase 2 com a ida em mãos (a fase 1 escolhe o VOO, sem
 * escada — nunca passa por aqui); multi quando TODOS os OUTROS trechos já têm
 * `fares`+`summary` gravados — o ativo é o único que falta.
 */
export function isFinalPick(state: ExplorerState): boolean {
  if (state.request.mode === 'one_way') return true;
  if (state.request.mode === 'round_trip') {
    return state.activeLeg === 1 && state.chosen[0] !== undefined;
  }
  return state.request.legs.every(
    (_, i) => i === state.activeLeg || Boolean(state.chosen[i]?.fares && state.chosen[i].summary),
  );
}

/**
 * O payload do "Confirmar seleção" — `null` enquanto a seleção não fecha.
 * one_way/RT ⇒ `single` (RT: o `offerId` é o ref de VOLTA, que carrega o par);
 * multi_leg ⇒ `multi` agregado, com UM fareId por perna (cada trecho é one-way)
 * e o total via `sumMoney` (mesma moeda por construção — todas as pernas saem
 * do MESMO BFF; se divergirem, o `sumMoney` se cala e o confirm não fecha).
 *
 * **No RT os `fares` são os da FASE 2 — a fase 1 nem tem tarifa, e isso é
 * contrato do provider, não esquecimento.** O `fareId` que fecha a compra é
 * resolvido contra o ref do PAR (`resolveModalPricingOptionId`): ou é um
 * `pricingOptionId` daquela captura (bundled), ou o par `departing_*|returning_*`
 * de linha e coluna DA MESMA matriz (split). Uma escada na fase 1 rodaria sobre
 * o ref da IDA, e o id dela seria um `pricingOptionId` one-way de OUTRA captura
 * (verificado no fixture: `flight_pricing_option_*`, sem prefixo `departing_`) —
 * casá-lo com um id da fase 2 produziria um fareId que o `splitModalFarePair`
 * recusa e que o modal-session resolveria como a compra da ida sozinha. Por isso
 * a fase 1 escolhe só o VOO (select-mode) e toda tarifa é da fase 2.
 */
export function explorerConfirm(state: ExplorerState): ExplorerConfirm | null {
  if (state.request.mode === 'multi_leg') {
    const picks = state.request.legs.map((_, i) => state.chosen[i]);
    const done = picks.filter((p): p is Required<ExplorerPick> => Boolean(p?.fares && p.summary));
    if (done.length !== picks.length) return null;
    if (done.some((p) => isPairFare(p.fares))) return null;
    const legs = done.map((p, i) => ({
      legIndex: i,
      offerId: p.offerId,
      fareId: fareIdOf(p.fares),
      price: { amount: p.summary.total, currency: p.summary.currency },
    }));
    const total = sumMoney(legs.map((l) => l.price));
    if (!total) return null;
    return { kind: 'multi', legs, total, trip: tripGeometryOf(state.request) };
  }
  if (state.request.mode === 'round_trip' && !state.chosen[0]) return null;
  const last = state.chosen[state.request.mode === 'round_trip' ? 1 : 0];
  if (!last?.fares || !last.summary) return null;
  return {
    kind: 'single',
    offerId: last.offerId,
    fares: last.fares,
    summary: last.summary,
    trip: tripGeometryOf(state.request),
  };
}
